import { MarkdownContent } from '@backstage/core-components';
import { storageApiRef, useApi } from '@backstage/core-plugin-api';
import { releaseNotesApiRef, useReleaseNotes } from '@internal/backstage-plugin-release-notes-react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import NewReleasesOutlinedIcon from '@material-ui/icons/NewReleasesOutlined';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDate } from './homeUtils';

const RELEASE_NOTES_PATH = '/release-notes';
const releaseNoteViewPath = (id: string) => `${RELEASE_NOTES_PATH}/view/${encodeURIComponent(id)}`;

const STORAGE_BUCKET = 'devhub-home';
const LAST_SEEN_RELEASE_KEY = 'whatsNewLastSeenReleaseId';
// Only pop the dialog for reasonably fresh releases so first-time visitors
// are not greeted with months-old news.
const DIALOG_MAX_AGE_DAYS = 30;

export type LatestReleaseNote = ReturnType<typeof useReleaseNotes>['releaseNotes']['results'][number];

/** Latest DevHub release note, or undefined while loading or on error (fails soft). */
export const useLatestReleaseNote = (): LatestReleaseNote | undefined => {
  const { releaseNotes, loading, error } = useReleaseNotes({ max: 1 });
  if (loading || error) return undefined;
  return releaseNotes.results[0];
};

const useStyles = makeStyles(theme => ({
  line: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 999,
    border: '1px solid rgba(255, 255, 255, 0.35)',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    maxWidth: '100%',
  },
  badge: {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.7rem',
  },
  title: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  date: {
    opacity: 0.8,
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: 'inherit',
    fontWeight: 700,
    textDecoration: 'underline',
  },
  linkIcon: {
    fontSize: 16,
  },
  dialogMeta: {
    marginBottom: theme.spacing(2),
  },
}));

/** Compact "What's new" line for the home page hero (latest DevHub release note). */
export const WhatsNewLine = ({ latest, className }: { latest?: LatestReleaseNote; className?: string }) => {
  const classes = useStyles();
  if (!latest) return null;

  return (
    <div className={className ? `${classes.line} ${className}` : classes.line}>
      <NewReleasesOutlinedIcon fontSize="small" />
      <Typography component="span" className={classes.badge}>
        What's new
      </Typography>
      <Typography component="span" variant="body2" className={classes.title}>
        {latest.title}
      </Typography>
      <Typography component="span" variant="body2" className={classes.date}>
        {formatDate(latest.created_at)}
      </Typography>
      <Link component={RouterLink} to={releaseNoteViewPath(latest.id)} variant="body2" className={classes.link}>
        See what's new
        <ArrowForwardIcon className={classes.linkIcon} />
      </Link>
    </div>
  );
};

/**
 * One-time "What's new" dialog shown the first time a user opens the home
 * page after a newer release note is published. The last seen release id is
 * kept in the Backstage storage API.
 */
export const WhatsNewDialog = ({ latest }: { latest?: LatestReleaseNote }) => {
  const classes = useStyles();
  const storageApi = useApi(storageApiRef);
  const releaseNotesApi = useApi(releaseNotesApiRef);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!latest) return;
    const snapshot = storageApi.forBucket(STORAGE_BUCKET).snapshot<string>(LAST_SEEN_RELEASE_KEY);
    if (snapshot.presence === 'unknown' || snapshot.value === latest.id) return;

    const published = Date.parse(latest.created_at);
    if (Number.isNaN(published)) return;
    const isFresh = Date.now() - published <= DIALOG_MAX_AGE_DAYS * 24 * 3600 * 1000;
    // Respect the release-notes plugin's own "seen" marker (banner dismissed).
    const alreadySeen = releaseNotesApi.lastSeenDate().toMillis() >= published;
    if (isFresh && !alreadySeen) {
      setOpen(true);
    }
  }, [latest, storageApi, releaseNotesApi]);

  if (!latest) return null;

  const close = () => {
    setOpen(false);
    storageApi.forBucket(STORAGE_BUCKET).set(LAST_SEEN_RELEASE_KEY, latest.id);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="devhub-whats-new-title">
      <DialogTitle id="devhub-whats-new-title">What's new in DevHub</DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6">{latest.title}</Typography>
        <Typography variant="caption" color="textSecondary" component="p" className={classes.dialogMeta}>
          {formatDate(latest.created_at)}
          {latest.publisher ? ` - ${latest.publisher}` : ''}
        </Typography>
        <MarkdownContent content={latest.body || latest.excerpt} dialect="gfm" />
      </DialogContent>
      <DialogActions>
        <Button component={RouterLink} to={releaseNoteViewPath(latest.id)} onClick={close}>
          Read full release notes
        </Button>
        <Button color="primary" variant="contained" onClick={close}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};
